import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, Popconfirm, message, Image, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import API from '../api/client';

const InstagramPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPosts, setPreviewPosts] = useState([]);
  const [previewName, setPreviewName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try { const res = await API.get('/instagram-feeds'); setData(res.data); }
    catch { message.error('Veriler yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record._id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await API.put(`/instagram-feeds/${editingId}`, values);
        message.success('Güncellendi');
      } else {
        await API.post('/instagram-feeds', values);
        message.success('Eklendi');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation */ }
  };

  const handleDelete = async (id) => {
    await API.delete(`/instagram-feeds/${id}`);
    message.success('Silindi');
    fetchData();
  };

  const handleRefresh = async (id, name) => {
    setRefreshingId(id);
    try {
      const res = await API.post(`/instagram-feeds/${id}/refresh`);
      message.success(`${name} - ${res.data.length} post yenilendi`);
    } catch {
      message.error('Yenileme başarısız');
    }
    setRefreshingId(null);
  };

  const handlePreview = async (id, name) => {
    try {
      const res = await API.get(`/instagram-feeds/${id}/posts`);
      setPreviewPosts(res.data);
      setPreviewName(name);
      setPreviewOpen(true);
    } catch {
      message.error('Postlar yüklenemedi');
    }
  };

  const columns = [
    { title: 'Tesis', dataIndex: 'facilityName', key: 'facilityName' },
    { title: 'Kullanıcı Adı', dataIndex: 'handle', key: 'handle' },
    { title: 'Profil Linki', dataIndex: 'link', key: 'link', ellipsis: true },
    {
      title: 'İşlem', key: 'actions', width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="Postları Gör">
            <Button icon={<EyeOutlined />} size="small" onClick={() => handlePreview(record._id, record.facilityName)} />
          </Tooltip>
          <Tooltip title="Postları Yenile">
            <Button
              icon={<ReloadOutlined />}
              size="small"
              loading={refreshingId === record._id}
              onClick={() => handleRefresh(record._id, record.facilityName)}
            />
          </Tooltip>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Instagram Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Hesap</Button>
      </div>
      <Table columns={columns} dataSource={data} rowKey="_id" loading={loading} />

      {/* Ekleme / Düzenleme Modal */}
      <Modal
        title={editingId ? 'Instagram Hesabı Düzenle' : 'Yeni Instagram Hesabı'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={600}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="facilityName" label="Tesis / İşletme Adı" rules={[{ required: true, message: 'Tesis adı gerekli' }]}>
            <Input placeholder="Dihiz Restoran" />
          </Form.Item>
          <Form.Item name="handle" label="Instagram Kullanıcı Adı" rules={[{ required: true, message: 'Kullanıcı adı gerekli' }]}>
            <Input placeholder="@dihizrestoran" />
          </Form.Item>
          <Form.Item name="link" label="Instagram Profil Linki">
            <Input placeholder="https://instagram.com/dihizrestoran" />
          </Form.Item>
          <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 8px' }}>
            Kullanıcı adı girildikten sonra postlar otomatik çekilir (public profil gereklidir).
          </p>
        </Form>
      </Modal>

      {/* Post Önizleme Modal */}
      <Modal
        title={`${previewName} - Instagram Postları`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width={700}
      >
        {previewPosts.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>
            Henüz post yok. Yenile butonuna basarak Instagram'dan çekmeyi deneyin.
          </p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {previewPosts.map((post, i) => (
              <a key={i} href={post.permalink} target="_blank" rel="noreferrer" style={{ display: 'block', aspectRatio: '1', overflow: 'hidden', borderRadius: 8 }}>
                <img
                  src={post.image}
                  alt={post.caption || 'Instagram post'}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </a>
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

export default InstagramPage;
